import { useState } from "react";
import { api, fmtMoney, CURRENCIES } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

export default function Valuation() {
    const [inputs, setInputs] = useState({
        currency: "GBP",
        ebitda: "",
        revenue: "",
        ebitda_multiple_low: 4, ebitda_multiple_mid: 6, ebitda_multiple_high: 8,
        revenue_multiple_low: 0.8, revenue_multiple_mid: 1.2, revenue_multiple_high: 2,
        dcf_fcf: "",
        dcf_growth: 0.05, dcf_discount: 0.12, dcf_years: 5, dcf_terminal_growth: 0.02,
        net_debt: "",
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const setF = (k) => (v) => setInputs((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    const calculate = async () => {
        setLoading(true);
        try {
            const payload = {
                ebitda: inputs.ebitda ? parseFloat(inputs.ebitda) : null,
                revenue: inputs.revenue ? parseFloat(inputs.revenue) : null,
                ebitda_multiple_low: parseFloat(inputs.ebitda_multiple_low),
                ebitda_multiple_mid: parseFloat(inputs.ebitda_multiple_mid),
                ebitda_multiple_high: parseFloat(inputs.ebitda_multiple_high),
                revenue_multiple_low: parseFloat(inputs.revenue_multiple_low),
                revenue_multiple_mid: parseFloat(inputs.revenue_multiple_mid),
                revenue_multiple_high: parseFloat(inputs.revenue_multiple_high),
                dcf_fcf: inputs.dcf_fcf ? parseFloat(inputs.dcf_fcf) : null,
                dcf_growth: parseFloat(inputs.dcf_growth),
                dcf_discount: parseFloat(inputs.dcf_discount),
                dcf_years: parseInt(inputs.dcf_years),
                dcf_terminal_growth: parseFloat(inputs.dcf_terminal_growth),
                net_debt: inputs.net_debt !== "" ? parseFloat(inputs.net_debt) : null,
            };
            const { data } = await api.post("/valuation/calculate", payload);
            setResult(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    const ccy = inputs.currency;

    return (
        <div data-testid="valuation-page">
            <PageHeader title="Valuation Tool" subtitle="Estimate company value across methods." />

            <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl">
                <div className="space-y-6">
                    <Card title="Inputs">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2 space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Revenue (TTM)</Label>
                                <Input type="number" className="font-mono" value={inputs.revenue} onChange={setF("revenue")} data-testid="revenue-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Currency</Label>
                                <Select value={inputs.currency} onValueChange={setF("currency")}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-3 space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">EBITDA (TTM)</Label>
                                <Input type="number" className="font-mono" value={inputs.ebitda} onChange={setF("ebitda")} data-testid="ebitda-input" />
                            </div>
                            <div className="col-span-3 space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Net debt (optional — for enterprise-to-equity bridge)</Label>
                                <Input type="number" className="font-mono" value={inputs.net_debt} onChange={setF("net_debt")} data-testid="net-debt-input" />
                            </div>
                        </div>
                    </Card>

                    <Card title="EBITDA multiples">
                        <div className="grid grid-cols-3 gap-3">
                            {["low", "mid", "high"].map((k) => (
                                <div key={k} className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{k}</Label>
                                    <Input type="number" step="0.5" className="font-mono" value={inputs[`ebitda_multiple_${k}`]} onChange={setF(`ebitda_multiple_${k}`)} />
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card title="Revenue multiples">
                        <div className="grid grid-cols-3 gap-3">
                            {["low", "mid", "high"].map((k) => (
                                <div key={k} className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{k}</Label>
                                    <Input type="number" step="0.1" className="font-mono" value={inputs[`revenue_multiple_${k}`]} onChange={setF(`revenue_multiple_${k}`)} />
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card title="Basic DCF">
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Starting Free Cash Flow</Label>
                                <Input type="number" className="font-mono" value={inputs.dcf_fcf} onChange={setF("dcf_fcf")} data-testid="dcf-fcf-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Growth rate</Label>
                                    <Input type="number" step="0.01" className="font-mono" value={inputs.dcf_growth} onChange={setF("dcf_growth")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Discount rate</Label>
                                    <Input type="number" step="0.01" className="font-mono" value={inputs.dcf_discount} onChange={setF("dcf_discount")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Years</Label>
                                    <Input type="number" className="font-mono" value={inputs.dcf_years} onChange={setF("dcf_years")} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Terminal growth</Label>
                                    <Input type="number" step="0.01" className="font-mono" value={inputs.dcf_terminal_growth} onChange={setF("dcf_terminal_growth")} />
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Button onClick={calculate} disabled={loading} className="w-full bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="calculate-valuation-btn">
                        <Calculator className="w-4 h-4 mr-2" /> {loading ? "Calculating…" : "Calculate valuation"}
                    </Button>
                </div>

                <div className="space-y-4">
                    {result ? (
                        <>
                            {result.summary.mid != null ? (
                                <Card title="Blended summary (enterprise value)">
                                    <div className="grid grid-cols-3 gap-3 text-center" data-testid="valuation-summary">
                                        <Pill label="Low" value={fmtMoney(result.summary.low, ccy)} />
                                        <Pill label="Mid" value={fmtMoney(result.summary.mid, ccy)} highlight />
                                        <Pill label="High" value={fmtMoney(result.summary.high, ccy)} />
                                    </div>
                                    {result.summary.equity_value && (
                                        <div className="grid grid-cols-3 gap-3 text-center mt-4 pt-4 border-t border-border">
                                            <Pill label="Equity low" value={fmtMoney(result.summary.equity_value.low, ccy)} />
                                            <Pill label="Equity mid" value={fmtMoney(result.summary.equity_value.mid, ccy)} highlight />
                                            <Pill label="Equity high" value={fmtMoney(result.summary.equity_value.high, ccy)} />
                                        </div>
                                    )}
                                </Card>
                            ) : (
                                <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground" data-testid="valuation-summary-empty">
                                    {result.summary.reason || "No valuation method had enough input to run."}
                                </div>
                            )}

                            {Object.entries(result.methods).map(([k, m]) => (
                                <Card key={k} title={m.label}>
                                    <div className="grid grid-cols-3 gap-3 text-center">
                                        <Pill label="Low" value={fmtMoney(m.low, ccy)} />
                                        <Pill label="Mid" value={fmtMoney(m.mid, ccy)} highlight />
                                        <Pill label="High" value={fmtMoney(m.high, ccy)} />
                                    </div>
                                </Card>
                            ))}

                            {Object.keys(result.methods).length === 0 && (
                                <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">
                                    Enter at least EBITDA, Revenue, or DCF FCF to compute a valuation.
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">
                            <Calculator className="w-6 h-6 mx-auto mb-2 opacity-40" />
                            Enter financials and click "Calculate valuation" to see the valuation range.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="border border-border rounded-md bg-white p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{title}</div>
            {children}
        </div>
    );
}

function Pill({ label, value, highlight }) {
    return (
        <div className={`p-3 rounded-md border ${highlight ? "bg-foreground text-background border-foreground" : "border-border bg-muted/30"}`}>
            <div className={`text-[10px] uppercase tracking-wider ${highlight ? "text-background/60" : "text-muted-foreground"}`}>{label}</div>
            <div className="font-mono text-lg font-medium tracking-tight">{value}</div>
        </div>
    );
}
